import { BabyOutlineIcon } from '@ikyucom/design-system-icons-react'
import { CheckBox } from 'macaron-frontend-ui'
import { twMerge } from 'tailwind-merge'
import { FilterChip } from './FilterChip'
import { FilterChipsWithPopover, useClose, useCommit, useDraft, useSetDraft } from './Popover'
import { createColor } from './criteria'
import { CheckboxPopup } from './popup'
import type { ChildUse, ChildUseCriteria, FilterCriteria } from './types'

export type UseSetChildUse = () => (childUse: ChildUseCriteria) => void

export function ChildUse({
  useFilterCriteria,
  useChildUse,
  useSetChildUse,
  useEnabledChildUse,
}: {
  useFilterCriteria: () => FilterCriteria | undefined
  useChildUse: () => ChildUse
  useSetChildUse: UseSetChildUse
  useEnabledChildUse: () => ChildUse
}) {
  const hasChildUse = useChildUse()
  const setChildUse = useSetChildUse()
  const criteria = useFilterCriteria()?.childUses
  const enabled = useEnabledChildUse()

  if (!hasChildUse.gradeSchoolers && !hasChildUse.preSchoolers) {
    return null
  }

  if (hasChildUse.gradeSchoolers && hasChildUse.preSchoolers) {
    return (
      <ChildUseWithPopover
        preSchoolers={criteria?.preSchoolers ?? false}
        gradeSchoolers={criteria?.gradeSchoolers ?? false}
        setChildUse={setChildUse}
        enabled={enabled}
      />
    )
  }

  if (hasChildUse.gradeSchoolers) {
    const color = createColor({
      enabled: enabled.gradeSchoolers,
      isActive: criteria?.gradeSchoolers,
    })
    return (
      <FilterChip
        onClick={() => setChildUse({ gradeSchoolers: !criteria?.gradeSchoolers })}
        color={color}
      >
        <div className="flex items-center gap-1">
          <BabyOutlineIcon
            width={13}
            height={13}
            className={twMerge(criteria?.gradeSchoolers ? 'text-marron-light' : 'text-neutral-600')}
          />
          小学生から可
        </div>
      </FilterChip>
    )
  }

  const color = createColor({ enabled: enabled.preSchoolers, isActive: criteria?.preSchoolers })
  return (
    <FilterChip
      onClick={() => setChildUse({ preSchoolers: !criteria?.preSchoolers })}
      color={color}
    >
      <div className="flex items-center gap-1">
        <BabyOutlineIcon
          width={13}
          height={13}
          className={twMerge(criteria?.preSchoolers ? 'text-marron-light' : 'text-neutral-600')}
        />
        乳幼児から可
      </div>
    </FilterChip>
  )
}

type DraftChildUse = {
  preSchoolers: boolean
  gradeSchoolers: boolean
}

function ChildUseWithPopover({
  preSchoolers,
  gradeSchoolers,
  setChildUse,
  enabled,
}: {
  preSchoolers: boolean
  gradeSchoolers: boolean
  setChildUse: (childUse: ChildUseCriteria) => void
  enabled: ChildUse
}) {
  const label = createChildUseLabel({ preSchoolers, gradeSchoolers })
  const data: DraftChildUse = { preSchoolers, gradeSchoolers }
  const color = createColor({
    enabled: enabled.gradeSchoolers || enabled.preSchoolers,
    isActive: preSchoolers || gradeSchoolers,
  })
  return (
    <FilterChipsWithPopover
      trigger={
        <>
          <BabyOutlineIcon
            width={13}
            height={13}
            className={color === 'active' ? 'text-marron-light' : 'text-neutral-600'}
          />
          {label}
        </>
      }
      data={data}
      color={color}
    >
      <Body setChildUse={setChildUse} />
    </FilterChipsWithPopover>
  )
}

function Body({ setChildUse }: { setChildUse: (childUse: ChildUseCriteria) => void }) {
  const draft = useDraft<DraftChildUse | undefined>()
  const set = useSetDraft<DraftChildUse>()
  const commit = useCommit(setChildUse)
  const close = useClose()
  return (
    <CheckboxPopup update={commit} cancel={close}>
      <CheckBox
        checked={draft?.preSchoolers}
        onChange={() => set((prev) => ({ ...prev, preSchoolers: !prev.preSchoolers }))}
        label="乳幼児可"
      />
      <CheckBox
        checked={draft?.gradeSchoolers}
        onChange={() => set((prev) => ({ ...prev, gradeSchoolers: !prev.gradeSchoolers }))}
        label="小学生から可"
      />
    </CheckboxPopup>
  )
}

function createChildUseLabel({
  preSchoolers,
  gradeSchoolers,
}: {
  preSchoolers: boolean
  gradeSchoolers: boolean
}) {
  if (preSchoolers && gradeSchoolers) {
    return '乳幼児可、他1件'
  }
  if (preSchoolers) {
    return '乳幼児可'
  }
  if (gradeSchoolers) {
    return '小学生から可'
  }
  return 'お子様の利用'
}
